import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Headphones, Pause, Play, Loader2 } from "lucide-react";

const VOICES = [
  { id: "onyx", label: "Deep · Male" },
  { id: "coral", label: "Warm · Female" },
  { id: "alloy", label: "Neutral" },
  { id: "fable", label: "Storyteller" },
];
const SPEEDS = [1, 1.25, 1.5];

export const ReadAloud = ({ moduleId, lessonIndex }) => {
  const [status, setStatus] = useState("idle"); // idle | loading | ready
  const [playing, setPlaying] = useState(false);
  const [voice, setVoice] = useState(() => localStorage.getItem("tts_voice") || "onyx");
  const [speed, setSpeed] = useState(1);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  const reset = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      URL.revokeObjectURL(audioRef.current.src);
      audioRef.current = null;
    }
    setStatus("idle");
    setPlaying(false);
    setProgress(0);
  };

  useEffect(() => reset, [moduleId, lessonIndex, voice]);

  const start = async () => {
    setStatus("loading");
    try {
      const r = await api.get(`/tts/lesson/${moduleId}/${lessonIndex}`, {
        params: { voice }, responseType: "blob",
      });
      const audio = new Audio(URL.createObjectURL(r.data));
      audio.playbackRate = speed;
      audio.ontimeupdate = () => setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
      audio.onended = () => { setPlaying(false); setProgress(1); };
      audioRef.current = audio;
      setStatus("ready");
      await audio.play();
      setPlaying(true);
    } catch (e) {
      setStatus("idle");
      toast.error("Could not prepare the narration — please try again.");
    }
  };

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return start();
    if (playing) { audio.pause(); setPlaying(false); }
    else { await audio.play(); setPlaying(true); }
  };

  const changeSpeed = (s) => {
    setSpeed(s);
    if (audioRef.current) audioRef.current.playbackRate = s;
  };

  const changeVoice = (v) => {
    localStorage.setItem("tts_voice", v);
    setVoice(v);
  };

  return (
    <div className="mt-6 border hairline bg-white px-4 py-3 flex flex-wrap items-center gap-3" data-testid="read-aloud">
      <button onClick={toggle} disabled={status === "loading"}
              className="btn-primary text-sm flex items-center gap-2" data-testid="read-aloud-btn">
        {status === "loading" ? <Loader2 size={14} className="animate-spin" />
          : playing ? <Pause size={14} /> : status === "ready" ? <Play size={14} /> : <Headphones size={14} />}
        {status === "loading" ? "Preparing audio…" : playing ? "Pause" : status === "ready" ? "Resume" : "Read to me"}
      </button>

      <div className="flex-1 min-w-[120px] h-1.5 bg-[color:var(--brand-surface2)]" data-testid="read-aloud-progress">
        <div className="h-full bg-[color:var(--brand-primary)] transition-[width]" style={{ width: `${progress * 100}%` }} />
      </div>

      <div className="flex items-center gap-1" data-testid="read-aloud-speeds">
        {SPEEDS.map(s => (
          <button key={s} onClick={() => changeSpeed(s)} data-testid={`read-aloud-speed-${s}`}
                  className={`text-xs px-2 py-1 border hairline ${speed === s ? "bg-[color:var(--brand-primary)] text-white border-[color:var(--brand-primary)]" : "text-neutral-500 hover:text-black"}`}>
            {s}x
          </button>
        ))}
      </div>

      <select value={voice} onChange={e => changeVoice(e.target.value)} data-testid="read-aloud-voice"
              className="text-xs border hairline bg-white px-2 py-1.5 focus:outline-none">
        {VOICES.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
      </select>
    </div>
  );
};
